"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

function BreadcrumbsAndTitle() {
  const { links, loading } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="h-5 w-48 animate-pulse rounded bg-muted"></div>
        <div className="h-10 w-64 animate-pulse rounded bg-muted"></div>
      </div>
    );
  }

  if (!links || links.length === 0) {
    return (
      <h1 className="font-default text-3xl font-bold tracking-tight text-foreground md:text-4xl">
        Trgovina
      </h1>
    );
  }

  const title = links[links.length - 1]?.title || "Trgovina";

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumb>
        <BreadcrumbList>
          {links.map((link, index) => {
            const isLast = index === links.length - 1;
            return (
              <React.Fragment key={link.href}>
                <BreadcrumbItem data-index={index}>
                  {isLast ? (
                    <BreadcrumbPage>{link.title}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={link.href}>{link.title}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator />}
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      <h1 className="font-default text-3xl font-bold tracking-tight text-foreground md:text-4xl">
        {title}
      </h1>
    </div>
  );
}

function SubcategoriesList() {
  const { terms, loading } = useEcommerceTerms();

  if (loading || !terms || terms.length === 0) return null;

  return (
    <div className="mt-6 flex flex-wrap gap-2">
      {terms.map((term, i) => (
        <Link
          key={term.id}
          to={term.link || "#"}
          className="inline-flex items-center justify-center rounded-full bg-secondary px-4 py-1.5 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
          data-index={i}
        >
          {term.name}
        </Link>
      ))}
    </div>
  );
}

export default function ArchiveTitleSection() {
  const isClient = typeof window !== "undefined";
  const current_wp_term_query_params =
    isClient && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPTermQueryParams()
      : { taxonomy: "product_cat" };
  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section
      id="archive-title-section"
      className="border-b border-border bg-background py-8 md:py-12"
    >
      <div className="container mx-auto px-4 md:px-6">
        <ShopBreadcrumbsProvider>
          <BreadcrumbsAndTitle />
        </ShopBreadcrumbsProvider>

        <EcommerceTermsProvider wp_term_query={wp_term_query}>
          <SubcategoriesList />
        </EcommerceTermsProvider>
      </div>
    </section>
  );
}